import type { JSX } from 'react';
import {
  FileDown,
  FilePlus2,
  FolderOpen,
  HelpCircle,
  Maximize,
  Redo2,
  Save,
  Undo2,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import type { Command } from '../commands.js';
import { useEditor } from '../state/store.js';

export interface CommandBarProps {
  readonly run: (id: string) => void;
  readonly commands: readonly Command[];
  readonly zoomText: string;
}

/**
 * Document and view controls only.
 *
 * The active-wall dropdown and the 3D button used to live here too, which is
 * how wall management ended up in four places and how the last step of the
 * job ended up as an unlabelled button beside Export. Walls belong to the
 * walls list; 3D is step three of the step bar.
 */
export function CommandBar({ run, commands, zoomText }: CommandBarProps): JSX.Element {
  // Narrow selectors: this used to subscribe to the whole store, so every
  // status change — one per brick laid — re-rendered the entire bar (F12).
  const project = useEditor((s) => s.project);
  const docRevision = useEditor((s) => s.docRevision);
  const savedRevision = useEditor((s) => s.savedRevision);
  void docRevision;
  const enabled = (id: string): boolean => {
    const command = commands.find((candidate) => candidate.id === id);
    return command?.enabled === undefined ? true : command.enabled();
  };
  const dirty = project.revision !== savedRevision;

  return (
    <header className="commandbar">
      <span className="wordmark">Brickwall</span>
      <button className="btn newproject" onClick={() => run('file.new')} title="New project (Ctrl+N)">
        <FilePlus2 size={15} /> <span className="label">New</span>
      </button>
      <button className="btn" onClick={() => run('file.open')} title="Open project (Ctrl+O)">
        <FolderOpen size={15} /> <span className="label">Open</span>
      </button>
      <button className="btn" onClick={() => run('file.save')} title="Save (Ctrl+S)">
        <Save size={15} /> <span className="label">Save</span>
      </button>
      <span className="sep" />
      <button
        className="btn icon"
        onClick={() => run('edit.undo')}
        disabled={!enabled('edit.undo')}
        title="Undo (Ctrl+Z)"
        aria-label="Undo"
      >
        <Undo2 size={15} />
      </button>
      <button
        className="btn icon"
        onClick={() => run('edit.redo')}
        disabled={!enabled('edit.redo')}
        title="Redo (Ctrl+Y)"
        aria-label="Redo"
      >
        <Redo2 size={15} />
      </button>
      <span className="sep zoomgroup" />
      <button
        className="btn icon zoomgroup"
        onClick={() => run('view.zoomOut')}
        aria-label="Zoom out"
      >
        <ZoomOut size={15} />
      </button>
      <span className="zoom zoomgroup">{zoomText}</span>
      <button
        className="btn icon zoomgroup"
        onClick={() => run('view.zoomIn')}
        aria-label="Zoom in"
      >
        <ZoomIn size={15} />
      </button>
      <button className="btn icon" onClick={() => run('view.fit')} title="Fit wall (F)" aria-label="Fit wall">
        <Maximize size={15} />
      </button>

      <span className="spacer" />

      <span className="projname" title={project.name}>
        {project.name}
        {dirty ? <span className="dot">●</span> : null}
      </span>
      <button className="btn" onClick={() => run('file.exportPdf')} title="Export (Ctrl+P)">
        <FileDown size={15} /> <span className="label">Export</span>
      </button>
      <button className="btn icon" onClick={() => run('help.controls')} title="Controls (F1)" aria-label="Help">
        <HelpCircle size={15} />
      </button>
    </header>
  );
}
